import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, MessageCircle } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { useAuth } from '../../context/useAuth';
import { userRoles } from '../dashboard/roles';
import { pickDefaultCycleId, resolveActiveCycle, sortCyclesByStart } from '../../utils/cycleUtils';
import { gymOptionsForD28dScopeFilter } from '../../utils/organizationView';
import D28dCycleTrainerReportTab from './D28dCycleTrainerReportTab';
import './d28d-admin-surfaces.css';

const TABS = [
  { id: 'users', label: 'Usuarios' },
  { id: 'trainers', label: 'Entrenadores' },
];

function yesNo(value) {
  return value ? 'Sí' : '—';
}

function trafficMeta(light) {
  if (light === 'green') {
    return { label: 'Verde', className: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
  }
  if (light === 'yellow') {
    return { label: 'Amarillo', className: 'bg-amber-100 text-amber-900 border-amber-300' };
  }
  if (light === 'red') {
    return { label: 'Rojo', className: 'bg-red-100 text-red-800 border-red-300' };
  }
  return { label: '—', className: 'bg-stone-100 text-stone-600 border-stone-200' };
}

function daysSinceLabel(days) {
  if (days == null) return 'Sin clases';
  if (Number(days) === 0) return 'Hoy';
  if (Number(days) === 1) return '1 día';
  return `${days} días`;
}

export default function D28dCycleReportsAdmin({ onBack = null, scopeGymId = null }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const roles = userRoles(user);
  const canFilterByGym = scopeGymId == null
    && roles.some((r) => ['super_admin', 'admin_d28d'].includes(r));
  const [gymFilterId, setGymFilterId] = useState('');
  const [gymOptions, setGymOptions] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState('');
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [tab, setTab] = useState('users');

  const effectiveGymId = scopeGymId != null && scopeGymId !== ''
    ? Number(scopeGymId)
    : (canFilterByGym && gymFilterId ? Number(gymFilterId) : null);

  const loadGymOptions = useCallback(async () => {
    if (!canFilterByGym) return;
    try {
      const resp = await api.get('/gyms');
      const list = Array.isArray(resp.data?.data) ? resp.data.data : (Array.isArray(resp.data) ? resp.data : []);
      // Una sola entrada "D28D" (value="") cubre plataforma + ancla; no listar plantilla ni ancla.
      setGymOptions(gymOptionsForD28dScopeFilter(list));
    } catch {
      setGymOptions([]);
    }
  }, [canFilterByGym]);

  useEffect(() => {
    loadGymOptions();
  }, [loadGymOptions]);

  // Ciclos por ámbito: ignorar respuestas viejas al cambiar gimnasio rápido (race 3ª+ vez).
  // Sede: sin gym_only → ciclos del padre/plataforma (ventana de fechas); el reporte lista
  // usuarios de la sede por solape de vigencia (v8.278), no por cycle_id en la cuenta.
  useEffect(() => {
    let cancelled = false;
    setReport(null);
    setCycleId('');
    setCycles([]);
    const selectedGym = gymOptions.find((g) => Number(g.id) === Number(effectiveGymId));
    const isSedeScope = Boolean(
      selectedGym?.parent_gym_id != null && selectedGym.parent_gym_id !== '',
    );
    let params;
    if (effectiveGymId) {
      params = { gym_id: effectiveGymId };
      if (!isSedeScope) params.gym_only = 'true';
    } else {
      params = { platform_only: 'true' };
    }
    api.get('/cycles', { params }).then((r) => {
      if (cancelled) return;
      const list = sortCyclesByStart(r.data?.data || r.data || []);
      setCycles(list);
      setCycleId(pickDefaultCycleId(list));
    }).catch(() => {
      if (cancelled) return;
      setCycles([]);
      setCycleId('');
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveGymId, gymOptions]);

  useEffect(() => {
    let cancelled = false;
    if (!cycleId) {
      setReport(null);
      return undefined;
    }
    const params = { cycle_id: cycleId };
    if (effectiveGymId) params.gym_id = effectiveGymId;
    api.get('/d28d/cycle/admin/report', { params })
      .then((r) => {
        if (cancelled) return;
        setReport(r.data?.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [cycleId, effectiveGymId]);

  const runOrchestration = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await api.post('/d28d/cycle/admin/orchestrate', {});
      setMsg(t('d28d.reports.orchestrated', 'Orquestación ejecutada') + `: ${r.data?.data?.advanced?.length || 0} avances`);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const users = report?.users || [];
  const trafficCounts = useMemo(() => ({
    green: users.filter((u) => u.traffic_light === 'green').length,
    yellow: users.filter((u) => u.traffic_light === 'yellow').length,
    red: users.filter((u) => u.traffic_light === 'red').length,
  }), [users]);
  const activeCycleId = useMemo(() => resolveActiveCycle(cycles)?.id, [cycles]);
  const selectedCycle = cycles.find((c) => String(c.id) === String(cycleId));
  const gymSelectOptions = useMemo(() => gymOptions, [gymOptions]);

  return (
    <div className="d28d-admin-surface surface-readable dashboard-main-view space-y-5">
      <header className="flex flex-wrap justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">{t('d28d.reports.title', 'Reportes por ciclo')}</h1>
          <p className="text-sm text-stone-600">
            {tab === 'trainers'
              ? t('d28d.reports.subtitle_trainers', 'Clases impartidas, asistencia y calificación por entrenador')
              : t('d28d.reports.subtitle', 'Usuarios activos, asistencia y semáforo')}
          </p>
        </div>
        {onBack ? (
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            {t('common.back', 'Volver')}
          </button>
        ) : null}
      </header>

      <div className="flex gap-1 border-b border-stone-200">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === id
                ? 'border-[var(--d28d-yellow)] text-stone-900'
                : 'border-transparent text-stone-500 hover:text-stone-800'
            }`}
            onClick={() => setTab(id)}
          >
            {t(`d28d.reports.tab.${id}`, label)}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        {canFilterByGym ? (
          <label className="block">
            <span className="ui-field__label">{t('users.gym', 'Gimnasio')}</span>
            <select
              className="ui-input mt-1 min-w-[14rem]"
              value={gymFilterId}
              onChange={(e) => setGymFilterId(e.target.value)}
            >
              <option value="">{t('d28d.reports.platform_scope', 'D28D')}</option>
              {gymSelectOptions.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {g.parent_gym_id ? `↳ ${g.nombre}` : g.nombre}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="block">
          <span className="ui-field__label">{t('d28d.reports.cycle', 'Ciclo')}</span>
          <select className="ui-input mt-1" value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
            {cycles.length === 0 ? (
              <option value="">{t('d28d.reports.no_cycles', 'Sin ciclos en este ámbito')}</option>
            ) : null}
            {cycles.map((c) => {
              const isActive = activeCycleId != null && Number(c.id) === Number(activeCycleId);
              return (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.startDate}){isActive ? ' · activo' : ''}
                </option>
              );
            })}
          </select>
        </label>
        {tab === 'users' ? (
          <button
            type="button"
            className="btn btn-secondary text-sm"
            disabled={busy}
            onClick={runOrchestration}
            title={t('d28d.reports.orchestration_hint', 'Proceso manual de fin de ciclo: avanza usuarios y envía avisos. Normalmente lo ejecuta el sistema automáticamente.')}
          >
            {t('d28d.reports.run_orchestration', 'Ejecutar cierre/avance de ciclo')}
          </button>
        ) : null}
      </div>
      {selectedCycle && activeCycleId != null && Number(selectedCycle.id) !== Number(activeCycleId) ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t('d28d.reports.viewing_past_cycle', 'Estás viendo un ciclo distinto al activo hoy. El ciclo activo es {name}.', {
            name: resolveActiveCycle(cycles)?.name || '',
          })}
        </p>
      ) : null}
      {msg && <p className="text-sm text-stone-700">{msg}</p>}

      {report?.cycle ? (
        <div className="flex items-center gap-2 text-sm text-stone-600">
          <CalendarDays className="w-4 h-4 shrink-0" aria-hidden />
          <span>
            {report.cycle.name}
            {report.cycle.day != null
              ? ` · Día ${report.cycle.day} de ${report.cycle.total_days || 28}`
              : ''}
            {report.cycle.ended ? ' · cerrado' : ''}
          </span>
        </div>
      ) : null}

      {tab === 'trainers' ? (
        <D28dCycleTrainerReportTab cycleId={cycleId} scopeGymId={effectiveGymId} />
      ) : null}

      {tab === 'users' && report?.summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-4">
            <p className="text-xs text-stone-500">Usuarios activos</p>
            <p className="text-2xl font-bold">{report.summary.active_users}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-stone-500">Semáforo verde</p>
            <p className="text-2xl font-bold text-emerald-700">{trafficCounts.green}</p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-stone-500">Semáforo amarillo / rojo</p>
            <p className="text-2xl font-bold text-amber-800">
              {trafficCounts.yellow}
              <span className="text-stone-400 font-normal"> / </span>
              <span className="text-red-700">{trafficCounts.red}</span>
            </p>
          </div>
          <div className="card p-4">
            <p className="text-xs text-stone-500">Clases programadas</p>
            <p className="text-2xl font-bold">{report.summary.classes_scheduled}</p>
          </div>
        </div>
      ) : null}

      {tab === 'users' ? (
      <div className="card overflow-x-auto">
        <div className="p-4 border-b border-stone-200">
          <h2 className="font-semibold text-stone-900">Usuarios inscritos en el ciclo</h2>
          <p className="text-xs text-stone-500 mt-1">
            {users.length
              ? `${users.length} usuario(s) con vigencia que cubre este ciclo (no exige clase ni login).`
              : 'No hay usuarios con vigencia en este ciclo.'}
          </p>
        </div>
        {users.length === 0 ? (
          <p className="p-4 text-sm text-stone-600">
            No hay cuentas D28D activas cuya vigencia cubra las fechas de este ciclo.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stone-500 border-b border-stone-200">
                <th className="p-3">Usuario</th>
                <th className="p-3">Plan</th>
                <th className="p-3">Fotos</th>
                <th className="p-3">Medidas</th>
                <th className="p-3">Prueba física</th>
                <th className="p-3">Asistencia</th>
                <th className="p-3">Inscritas</th>
                <th className="p-3">Asistió</th>
                <th className="p-3">Semáforo</th>
                <th className="p-3">Última clase</th>
                <th className="p-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const meta = trafficMeta(u.traffic_light);
                return (
                  <tr key={u.user_id} className="border-t border-stone-100">
                    <td className="p-3">
                      <p className="font-medium text-stone-900">{u.user_name || `#${u.user_id}`}</p>
                      <p className="text-xs text-stone-500">{u.user_email || `ID ${u.user_id}`}</p>
                    </td>
                    <td className="p-3">{u.plan || '—'}</td>
                    <td className="p-3">{u.photos_slots_filled ?? 0}/{u.photos_required ?? 4}</td>
                    <td className="p-3">{yesNo(u.has_measures)}</td>
                    <td className="p-3">{u.fitness_total != null ? `${u.fitness_total} pts` : '—'}</td>
                    <td className="p-3 font-medium">{u.attendance_pct != null ? `${u.attendance_pct}%` : '—'}</td>
                    <td className="p-3">{u.classes_enrolled ?? 0}</td>
                    <td className="p-3">{u.classes_attended ?? 0}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.className}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3">
                      <p className="font-medium text-stone-900">{daysSinceLabel(u.days_since_last_class)}</p>
                      {u.last_attendance ? (
                        <p className="text-xs text-stone-500">{u.last_attendance}</p>
                      ) : null}
                    </td>
                    <td className="p-3">
                      {u.wa_url ? (
                        <a
                          href={u.wa_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5"
                          title={t('users.whatsapp_contact', 'Contactar por WhatsApp')}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          WhatsApp
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary text-xs px-2.5 py-1.5 opacity-50 cursor-not-allowed"
                          disabled
                          title={t('users.whatsapp_no_phone', 'Sin teléfono registrado — edita el usuario para habilitar WhatsApp')}
                        >
                          WhatsApp
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      ) : null}
    </div>
  );
}
